import { Fragment, useMemo } from "react";
import countries from "i18n-iso-countries";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { FormAutocomplete } from "@components/inputs/form/FormAutocomplete/FormAutocomplete";
import { FormDatepicker } from "@components/inputs/form/FormDatepicker/FormDatepicker";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useSubmitPoliticalEffectedPeopleMutation } from "@services/knowYourCustomer/mutations";
import { usePoliticalEffectedPeopleQuery } from "@services/knowYourCustomer/queries";
import {
  KycFormStep,
  type IPoliticalEffectedPeople,
  type TKnowYourCustomerStepAction,
} from "@services/knowYourCustomer/types";
import { useCurrentLanguageCode } from "@utils/common";
import { formatDate } from "@utils/units";
import { COUNTRY_CODE } from "@/constants";
import { KYCModalActionButtons } from "../KYCModalActionButtons/KYCModalActionButtons";
import { KYCModalPaging } from "../KYCModalPaging/KYCModalPaging";

interface IPoliticalEffectedPeopleFormProps {
  onChangeStep: (step: KycFormStep) => void;
}

export const PoliticalEffectedPeopleForm: React.FC<
  IPoliticalEffectedPeopleFormProps
> = ({ onChangeStep }) => {
  const { t, i18n } = useTranslation();
  const { data: politicalEffectedPeople } = usePoliticalEffectedPeopleQuery();
  const datePickerPlaceholder = formatDate(new Date());
  const currentLanguageCode = useCurrentLanguageCode();

  const politicalEffectedPeopleDTO: IPoliticalEffectedPeople = useMemo(() => {
    return {
      politicalEffectedPeople: [
        {
          firstName: "",
          lastName: "",
          dateOfBirth: null,
          countryCode: "",
          institution: "",
          responsibilities: "",
          relationType: null,
          politicalRole: null,
          workingFrom: null,
          workingTo: null,
        },
      ],
    };
  }, []);

  const { mutateAsync: submitPoliticalEffectedPeople, isPending } =
    useSubmitPoliticalEffectedPeopleMutation();

  const {
    relationTypeOptions,
    politicalRoleOptions,
    countryOptions,
    defaultValues,
  } = useMemo(() => {
    if (!politicalEffectedPeople) {
      return {
        relationTypeOptions: [],
        politicalRoleOptions: [],
        countryOptions: [],
        defaultValues: undefined,
      };
    }

    const relationTypeOptions = (
      politicalEffectedPeople.relationTypes ?? []
    ).map((relationType) => ({
      value: relationType,
      label: t(
        `KYC.modal.step6.form.fields.relationType.options.${relationType}`,
      ),
    }));

    const politicalRoleOptions = (
      politicalEffectedPeople.politicalRoles ?? []
    ).map((politicalRole) => ({
      value: politicalRole,
      label: t(
        `KYC.modal.step6.form.fields.politicalRole.options.${politicalRole}`,
      ),
    }));

    let countryOptions = (politicalEffectedPeople.countryCodes ?? [])
      .map((country) => {
        const existsInTranslations = i18n.exists(`country.${country.name}`);

        const fallbackCountryName = existsInTranslations
          ? t(`country.${country.name}`)
          : country.name;

        const countryName =
          countries.getName(country.code, currentLanguageCode) ??
          fallbackCountryName;

        return {
          value: country.code,
          label: countryName,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    // move current country to top of the list
    const currentCountry = (politicalEffectedPeople.countryCodes ?? []).find(
      (i) => i.code === COUNTRY_CODE.toUpperCase(),
    );
    const currentCountryOptionIndex = (countryOptions ?? []).findIndex(
      (i) => i.value === currentCountry?.code,
    );
    const currentCountryOption = countryOptions[currentCountryOptionIndex];
    if (currentCountryOption) {
      countryOptions.splice(currentCountryOptionIndex, 1);
      countryOptions = [currentCountryOption, ...countryOptions];
    }

    const defaultValues = politicalEffectedPeople.politicalEffectedPeople.length
      ? politicalEffectedPeople
      : politicalEffectedPeopleDTO;

    return {
      relationTypeOptions,
      politicalRoleOptions,
      countryOptions,
      defaultValues,
    };
  }, [
    politicalEffectedPeople,
    t,
    politicalEffectedPeopleDTO,
    currentLanguageCode,
  ]);

  const { handleSubmit, control, setError } = useForm<IPoliticalEffectedPeople>(
    {
      values: defaultValues,
    },
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: "politicalEffectedPeople",
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitPoliticalEffectedPeopleForm = async (
    data: IPoliticalEffectedPeople,
    step?: TKnowYourCustomerStepAction,
  ) => {
    delete data.countryCodes;
    delete data.politicalRoles;
    delete data.relationTypes;

    await submitPoliticalEffectedPeople({ ...data, step })
      .then((response) => onChangeStep(response.currentForm))
      .catch(setApiErrors);
  };

  return (
    <div className="flex flex-col gap-6">
      <KYCModalPaging
        currentStep={KycFormStep.POLITICAL_AFFECTED_PEOPLE_FORM}
        onPreviousButtonClick={handleSubmit((data) =>
          submitPoliticalEffectedPeopleForm(data, "previous"),
        )}
        onNextButtonClick={handleSubmit((data) =>
          submitPoliticalEffectedPeopleForm(data),
        )}
        isLoading={isPending}
      />
      <form
        onSubmit={handleSubmit((data) =>
          submitPoliticalEffectedPeopleForm(data),
        )}
        className="flex flex-col gap-3"
      >
        {fields.map((field, index) => (
          <Fragment key={field.id}>
            <p className="text-gray-900 p-0 text-lg font-semibold">
              {t("KYC.modal.step6.title")}
            </p>
            <FormSelect
              name={`politicalEffectedPeople.${index}.politicalRole`}
              options={politicalRoleOptions}
              control={control}
              label={t("KYC.modal.step6.form.fields.politicalRole.label")}
              shouldTruncateOptions={false}
            />
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              <FormInput
                name={`politicalEffectedPeople.${index}.firstName`}
                control={control}
                label={t("KYC.modal.step6.form.fields.firstName.label")}
                placeholder={t(
                  "KYC.modal.step6.form.fields.firstName.placeholder",
                )}
              />
              <FormInput
                name={`politicalEffectedPeople.${index}.lastName`}
                control={control}
                label={t("KYC.modal.step6.form.fields.lastName.label")}
                placeholder={t(
                  "KYC.modal.step6.form.fields.lastName.placeholder",
                )}
              />
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              <FormDatepicker
                name={`politicalEffectedPeople.${index}.dateOfBirth`}
                control={control}
                label={t("KYC.modal.step6.form.fields.dateOfBirth.label")}
                placeholder={datePickerPlaceholder}
              />
              <FormAutocomplete
                name={`politicalEffectedPeople.${index}.countryCode`}
                options={countryOptions}
                control={control}
                label={t("KYC.modal.step6.form.fields.countryCode.label")}
                placeholder={t(
                  "KYC.modal.step6.form.fields.countryCode.placeholder",
                )}
                className="w-full"
              />
            </div>
            <FormInput
              name={`politicalEffectedPeople.${index}.institution`}
              control={control}
              label={t("KYC.modal.step6.form.fields.institution.label")}
              placeholder={t(
                "KYC.modal.step6.form.fields.institution.placeholder",
              )}
            />
            <FormInput
              name={`politicalEffectedPeople.${index}.responsibilities`}
              control={control}
              label={t("KYC.modal.step6.form.fields.responsibilities.label")}
              placeholder={t(
                "KYC.modal.step6.form.fields.responsibilities.placeholder",
              )}
            />
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              <FormDatepicker
                name={`politicalEffectedPeople.${index}.workingFrom`}
                control={control}
                label={t("KYC.modal.step6.form.fields.workingFrom.label")}
                placeholder={datePickerPlaceholder}
              />
              <FormDatepicker
                name={`politicalEffectedPeople.${index}.workingTo`}
                control={control}
                label={t("KYC.modal.step6.form.fields.workingTo.label")}
                placeholder={datePickerPlaceholder}
              />
            </div>
            <FormSelect
              name={`politicalEffectedPeople.${index}.relationType`}
              options={relationTypeOptions}
              control={control}
              label={t("KYC.modal.step6.form.fields.relationType.label")}
              shouldTruncateOptions={false}
            />
            <div className="flex gap-4">
              {index !== 0 && (
                <Button
                  variant="secondary"
                  className="w-full"
                  type="button"
                  onClick={() => remove(index)}
                >
                  {t("KYC.modal.step6.form.buttons.remove")}
                </Button>
              )}
              {fields.length === index + 1 && (
                <Button
                  className="w-full"
                  type="button"
                  onClick={() =>
                    append(politicalEffectedPeopleDTO.politicalEffectedPeople)
                  }
                >
                  {t("KYC.modal.step6.form.buttons.add")}
                </Button>
              )}
            </div>
          </Fragment>
        ))}
      </form>
      <KYCModalActionButtons
        onSubmit={handleSubmit((data) =>
          submitPoliticalEffectedPeopleForm(data),
        )}
        onCancel={handleSubmit((data) =>
          submitPoliticalEffectedPeopleForm(data, "close"),
        )}
        isLoading={isPending}
      />
    </div>
  );
};
